import type { BrandIntelligence, BrandMetric } from '@/features/reports/api';
import {
    changeText,
    interestNote,
    interestValue,
    sellThroughNote,
    sellThroughValue,
    topClientsNote,
    topClientsValue,
} from '@/features/reports/brands/labels';
import { BRANDS_COPY as COPY } from '@/features/reports/copy';
import { formatPercent, formatPounds } from '@/features/reports/format';
import { KpiTile, Tile } from '@/features/reports/parts';

/**
 * Brand intelligence's two rows (ALS-91): Demand's three tiles and Commercial's four, each with
 * its definition behind an (i). A figure that cannot be worked out is a dash with the reason,
 * never a zero. One column on a phone, two on a tablet, the prototype's three and four on a desk.
 */
export function BrandTiles({
    demand,
    commercial,
    previous,
}: {
    demand: NonNullable<BrandIntelligence['demand']>;
    commercial: NonNullable<BrandIntelligence['commercial']>;
    /** The quarter before's label, which Client interest is against. */
    previous: string | null;
}) {
    const rate = (label: string, definition: string, metric: BrandMetric) => (
        <KpiTile
            label={label}
            definition={definition}
            metric={metric}
            format={formatPercent}
            unit="pt"
            changeText={changeText(metric)}
        />
    );

    return (
        <div className="flex min-w-0 flex-col gap-3">
            <h3 className="text-label-md text-muted-foreground">{COPY.groups.demand}</h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <Tile
                    label={COPY.tiles.interest.label}
                    definition={COPY.tiles.interest.definition}
                    value={interestValue(demand.interest)}
                    note={interestNote(demand.interest, previous)}
                />
                <Tile
                    label={COPY.tiles.sellThrough.label}
                    definition={COPY.tiles.sellThrough.definition}
                    value={sellThroughValue(demand.sellThrough)}
                    note={sellThroughNote(demand.sellThrough)}
                />
                <Tile
                    label={COPY.tiles.topClients.label}
                    definition={COPY.tiles.topClients.definition}
                    value={topClientsValue(demand.topClients)}
                    note={topClientsNote(demand.topClients)}
                />
            </div>

            <h3 className="text-label-md text-muted-foreground mt-2">{COPY.groups.commercial}</h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {rate(
                    COPY.tiles.conversion.label,
                    COPY.tiles.conversion.definition,
                    commercial.conversion,
                )}
                <KpiTile
                    label={COPY.tiles.averagePrice.label}
                    definition={COPY.tiles.averagePrice.definition}
                    metric={commercial.averagePrice}
                    format={formatPounds}
                    changeText={changeText(commercial.averagePrice)}
                />
                {rate(
                    COPY.tiles.repeatRate.label,
                    COPY.tiles.repeatRate.definition,
                    commercial.repeatRate,
                )}
                {rate(
                    COPY.tiles.walletShare.label,
                    COPY.tiles.walletShare.definition,
                    commercial.walletShare,
                )}
            </div>
        </div>
    );
}
